import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandInfo } from '@/mocks/brand';


export default function AboutPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'brand' | 'reputation' | 'contact'>('brand');

  const baseUrl = 'https://www.kaiyuansports.com';
  const pageTitle = '关于开元体育 - 品牌介绍、口碑荣誉与联系方式 | 专业运动平台';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '开元体育是一个什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开元体育是国内领先的专业体育运动平台，提供体育赛事资讯、运动健身指导及体育娱乐服务，致力于为运动爱好者打造一站式运动生态体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在开元体育上注册会员？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过开元体育官网或App进行注册。在首页点击"立即加入"按钮，填写基本信息即可完成注册，注册完全免费。',
        },
      },
      {
        '@type': 'Question',
        name: '开元体育提供哪些运动项目的内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开元体育覆盖足球、篮球、网球、游泳、跑步、健身、瑜伽、羽毛球等多个运动项目，提供赛事资讯、技术指导、训练方案等全方位内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系开元体育的客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过客服电话400-888-9999、发送邮件至contact@kaiyuansports.com，或通过网站上的联系表单与我们取得联系。',
        },
      },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于开元体育',
    description: brandInfo.description,
    url: `${baseUrl}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: '开元体育',
      url: baseUrl,
    },
    about: {
      '@type': 'Organization',
      name: brandInfo.name,
      description: brandInfo.description,
      foundingDate: '2015',
      url: baseUrl,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-9999',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={`${brandInfo.description} 了解开元体育的品牌故事、发展历程、行业荣誉及联系方式。`}
        keywords="开元体育,关于开元,体育平台介绍,品牌故事,联系方式,开元体育口碑"
        canonicalUrl={`${baseUrl}/about`}
        schemaJson={aboutSchema}
      />

      {activeTab === 'contact' && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <div className="w-full px-4 md:px-6 lg:px-8 pt-20 pb-4 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: t('breadcrumb.about') }]} />
        </div>
      </div>

      <section className="w-full px-4 md:px-6 lg:px-8 py-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-1">
            {t('about.title')}
          </h1>
          <p className="text-sm text-foreground-500">{brandInfo.description}</p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-1">
            {(['brand', 'reputation', 'contact'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-3 text-sm font-medium transition-colors duration-200 border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? 'text-primary-500 border-primary-500'
                    : 'text-foreground-500 border-transparent hover:text-foreground-700'
                }`}
              >
                {tab === 'brand' && t('about.brand')}
                {tab === 'reputation' && t('about.reputation')}
                {tab === 'contact' && t('about.contact')}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'brand' && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-14">
                <div>
                  <div className="relative w-full h-64 md:h-80 rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20bright%20office%20space%20with%20sports%20theme%20decoration%2C%20clean%20minimalist%20design%2C%20large%20windows%20natural%20light%2C%20team%20collaboration%20area%2C%20warm%20wooden%20elements%2C%20professional%20corporate%20atmosphere%2C%20energetic%20workspace&width=800&height=600&seq=about-brand-office&orientation=landscape"
                      alt="开元体育办公室"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-5">
                    {t('about.brand')}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                    {brandInfo.description}
                  </p>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-background-100 rounded-xl p-4 text-center">
                      <p className="text-xs text-foreground-400 mb-1">{t('about.founded')}</p>
                      <p className="text-lg font-bold text-foreground-900 font-heading">{brandInfo.founded}</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-4 text-center">
                      <p className="text-xs text-foreground-400 mb-1">{t('about.location')}</p>
                      <p className="text-lg font-bold text-foreground-900 font-heading">北京</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-4 text-center">
                      <p className="text-xs text-foreground-400 mb-1">{t('about.team')}</p>
                      <p className="text-lg font-bold text-foreground-900 font-heading">{brandInfo.teamSize}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-focus-3-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">使命</p>
                        <p className="text-sm text-foreground-500">{brandInfo.mission}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-accent-100 rounded-lg">
                        <i className="ri-eye-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">愿景</p>
                        <p className="text-sm text-foreground-500">{brandInfo.vision}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-8 text-center">
                发展历程
              </h3>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/70 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {brandInfo.milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className={`flex flex-col md:flex-row gap-4 md:gap-8 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="bg-background-100 rounded-xl p-5">
                          <span className="inline-block px-3 py-1 text-xs font-bold text-accent-700 bg-accent-100 rounded-full mb-2">
                            {m.year}
                          </span>
                          <h4 className="text-sm font-semibold text-foreground-900 mb-1">{m.title}</h4>
                          <p className="text-xs md:text-sm text-foreground-500">{m.description}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex w-8 shrink-0 items-start justify-center pt-5">
                        <div className="w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50"></div>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reputation' && (
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8 text-center">
                {t('about.reputation')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {brandInfo.honors.map((h, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-accent-300/60 transition-all duration-300"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                      <i className="ri-award-line text-xl text-accent-600"></i>
                    </div>
                    <span className="text-xs font-bold text-accent-600 mb-2 block">{h.year}</span>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{h.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{h.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                    {t('about.contact')}
                  </h2>
                  <div className="space-y-5 mb-8">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-primary-100 rounded-xl">
                        <i className="ri-phone-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">客服热线</p>
                        <p className="text-sm text-foreground-500">400-888-9999</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-accent-100 rounded-xl">
                        <i className="ri-mail-line text-lg text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-500">contact@kaiyuansports.com</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-secondary-100 rounded-xl">
                        <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">公司地址</p>
                        <p className="text-sm text-foreground-500">北京市朝阳区体育中心A座16层</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-secondary-100 rounded-xl">
                        <i className="ri-time-line text-lg text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">工作时间</p>
                        <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4">常见问题</h3>
                    <div className="space-y-4">
                      <div className="bg-background-100 rounded-xl p-4">
                        <p className="text-sm font-semibold text-foreground-900 mb-1">开元体育是一个什么样的平台？</p>
                        <p className="text-xs md:text-sm text-foreground-500">开元体育是国内领先的专业体育运动平台，提供体育赛事资讯、运动健身指导及体育娱乐服务，致力于为运动爱好者打造一站式运动生态体验。</p>
                      </div>
                      <div className="bg-background-100 rounded-xl p-4">
                        <p className="text-sm font-semibold text-foreground-900 mb-1">如何在开元体育上注册会员？</p>
                        <p className="text-xs md:text-sm text-foreground-500">您可以通过开元体育官网或App进行注册。在首页点击"立即加入"按钮，填写基本信息即可完成注册，注册完全免费。</p>
                      </div>
                      <div className="bg-background-100 rounded-xl p-4">
                        <p className="text-sm font-semibold text-foreground-900 mb-1">开元体育提供哪些运动项目的内容？</p>
                        <p className="text-xs md:text-sm text-foreground-500">开元体育覆盖足球、篮球、网球、游泳、跑步、健身、瑜伽、羽毛球等多个运动项目，提供赛事资讯、技术指导、训练方案等全方位内容。</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70" id="contact">
                    <h3 className="text-lg font-bold text-foreground-900 mb-6">给我们留言</h3>
                    <ContactForm t={t} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function ContactForm({ t }: { t: (key: string) => string }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotValue = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setStatus('success');
      form.reset();
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const formData = new URLSearchParams();
      const data = new FormData(form);
      data.forEach((value, key) => {
        if (key !== 'website_alt') {
          formData.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9rfi27nahb7382ovllg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setErrorMsg(serverMsg || t('contact.error'));
        setStatus('error');
      }
    } catch {
      setErrorMsg(t('contact.error'));
      setStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="true">
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.name')}</label>
          <input
            type="text"
            name="name"
            required
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors duration-200"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.email')}</label>
          <input
            type="email"
            name="email"
            required
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors duration-200"
            placeholder="请输入您的邮箱"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.phone')}</label>
          <input
            type="tel"
            name="phone"
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors duration-200"
            placeholder="请输入您的电话（选填）"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.message')}</label>
          <textarea
            name="message"
            required
            maxLength={500}
            rows={4}
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors duration-200 resize-none"
            placeholder="请输入您的留言（500字以内）"
          ></textarea>
        </div>
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full py-3 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-200 disabled:opacity-60 whitespace-nowrap"
        >
          {status === 'loading' ? '提交中...' : t('contact.submit')}
        </button>
        {status === 'success' && (
          <p className="text-sm text-secondary-600 text-center">{t('contact.success')}</p>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-500 text-center">{errorMsg}</p>
        )}
      </div>
    </form>
  );
}